import { Link, NavLink, useNavigate } from "react-router-dom";
import { Plus, Settings, SquarePen } from "lucide-react";
import { BrandLockup } from "@/components/brand/Logo";
import { PlanCard } from "@/components/layout/PlanCard";
import { AccountButton } from "@/components/layout/AccountMenu";
import { RecentList } from "@/components/layout/RecentList";
import { NAV_ORDER, SECTIONS } from "@/lib/sections";
import { cn } from "@/lib/utils";

function NavItem({ section, sectionKey }) {
  const Icon = section.icon;
  return (
    <NavLink
      to={section.path}
      end={section.path === "/"}
      data-testid={`nav-item-${sectionKey}`}
      className={({ isActive }) =>
        cn(
          "group flex h-9 items-center gap-3 rounded-lg px-3 text-[14px] transition-colors duration-150",
          isActive ? "bg-white/[0.07] text-white" : "text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100",
        )
      }
    >
      <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.6} />
      <span className="truncate">{section.label}</span>
      {!section.live && <span className="ml-auto text-[10px] uppercase tracking-[0.14em] text-zinc-600 group-hover:text-zinc-500">Soon</span>}
    </NavLink>
  );
}

export function Sidebar({ me }) {
  const navigate = useNavigate();
  return (
    <div className="flex h-full w-[270px] flex-col">
      <div className="flex h-16 shrink-0 items-center justify-between px-5">
        <Link to="/" aria-label="ChatSleek home" data-testid="sidebar-logo-link">
          <BrandLockup size={28} />
        </Link>
      </div>
      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={() => navigate("/", { state: { focusComposer: Date.now() } })}
          data-testid="new-chat-button"
          className="flex h-10 w-full items-center gap-2.5 rounded-xl border border-white/[0.09] bg-ink-850 px-3 text-[14px] font-medium text-zinc-100 transition-colors hover:border-white/[0.16] hover:bg-ink-800"
        >
          <SquarePen className="h-4 w-4" strokeWidth={1.7} />
          New chat
          <Plus className="ml-auto h-4 w-4 text-zinc-500" strokeWidth={1.6} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        <nav className="space-y-0.5 pt-1" aria-label="Main">
          {NAV_ORDER.map((key) => (
            <NavItem key={key} sectionKey={key} section={SECTIONS[key]} />
          ))}
        </nav>
        <RecentList />
      </div>
      <div className="shrink-0 space-y-2 border-t border-white/[0.06] px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-3">
        <NavLink
          to="/settings"
          data-testid="nav-item-settings"
          className={({ isActive }) =>
            cn("flex h-9 items-center gap-3 rounded-lg px-3 text-[14px] transition-colors", isActive ? "bg-white/[0.07] text-white" : "text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100")
          }
        >
          <Settings className="h-[17px] w-[17px]" strokeWidth={1.6} />
          Settings
        </NavLink>
        <PlanCard me={me} />
        <AccountButton me={me} />
      </div>
    </div>
  );
}
